<template>
  <b-modal
    ref="modal"
    title="Manual Result SGM"
    centered
    size="md"
    cancel-variant="outline-secondary"
    hide-footer
    @hide="hideModal"
  >
    <div style="color: #1a1a1a">
      <!-- Bet header -->
      <div style="font-size: 12px; color: #6b7280; margin-bottom: 2px">
        Betslip:
        <strong style="color: #1a1a1a">{{
          selectedBet ? selectedBet.displayId || selectedBet.id : "-"
        }}</strong>
      </div>
      <div style="font-size: 12px; color: #6b7280; margin-bottom: 14px">
        Same Game Multi @<strong style="color: #1a1a1a">{{
          groupOdds != null ? Number(groupOdds).toFixed(2) : "N/A"
        }}</strong>
      </div>

      <!-- SGM Result -->
      <div
        style="
          font-weight: 700;
          font-size: 13px;
          padding-bottom: 6px;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 10px;
        "
      >
        SGM Result
      </div>
      <b-form-radio-group
        v-model="groupResult"
        :options="optionsResult"
        name="sgm-group-result"
        style="margin-bottom: 16px; font-size: 13px"
      />

      <!-- Legs -->
      <div
        style="
          font-weight: 700;
          font-size: 13px;
          padding-bottom: 6px;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 10px;
        "
      >
        Legs
      </div>
      <div
        v-for="(leg, idx) in legs"
        :key="leg.betslipEventId || idx"
        :style="{
          paddingBottom: idx < legs.length - 1 ? '10px' : '0',
          marginBottom: idx < legs.length - 1 ? '10px' : '0',
          borderBottom:
            idx < legs.length - 1 ? '1px dashed #e5e7eb' : 'none',
        }"
      >
        <div style="font-size: 12px; color: #374151; margin-bottom: 6px">
          {{ idx + 1 }}. {{ leg.marketName ? leg.marketName + " - " : ""
          }}{{ leg.name }}
          @<strong style="color: #111827">{{
            leg.odds != null && leg.odds !== 0
              ? Number(leg.odds).toFixed(2)
              : "N/A"
          }}</strong>
        </div>
        <b-form-radio-group
          v-model="leg.result"
          :options="optionsResult"
          :name="`leg-result-${idx}`"
          style="font-size: 13px"
        />
      </div>

      <!-- Actions -->
      <div class="d-flex" style="gap: 8px; margin-top: 18px">
        <b-button
          variant="outline-secondary"
          style="flex: 1"
          @click="$refs.modal.hide()"
          >Cancel</b-button
        >
        <b-button
          variant="primary"
          style="flex: 2"
          :disabled="!canSave || loadingManualResultSgm"
          @click="handleSave"
        >
          <Spinner v-if="loadingManualResultSgm" />
          <span v-else>Save</span>
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BButton, BFormRadioGroup } from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";
import { objectProp } from "@/helpers/props";
import Spinner from "@/@core/components/Spinner.vue";

const RESULT_MAP = {
  Win: "resulted-win",
  Lose: "resulted-loss",
  Refunded: "bet-refunded",
  Void: "bet-refunded",
  Draw: "resulted-draw",
};

export default {
  components: {
    BModal,
    BButton,
    BFormRadioGroup,
    Spinner,
  },
  props: {
    selectedBet: objectProp(),
  },
  data() {
    return {
      sgmGroupId: null,
      groupOdds: null,
      groupResult: null,
      legs: [],
      optionsResult: [
        { value: "resulted-win", text: "Win" },
        { value: "resulted-loss", text: "Lose" },
        { value: "bet-refunded", text: "Void" },
        { value: "resulted-draw", text: "Draw" },
        { value: "bet-pending", text: "UnResult" },
      ],
    };
  },
  computed: {
    ...mapState("trading/betslip", ["loadingManualResultSgm"]),
    canSave() {
      return (
        !!this.groupResult &&
        this.legs.length > 0 &&
        this.legs.every((l) => !!l.result)
      );
    },
  },
  watch: {
    legs: {
      deep: true,
      handler(newLegs) {
        const results = newLegs.map((l) => l.result).filter(Boolean);
        if (results.length !== newLegs.length) return;
        if (results.includes("resulted-loss")) this.groupResult = "resulted-loss";
        else if (results.every((r) => r === "resulted-win"))
          this.groupResult = "resulted-win";
        else if (results.every((r) => r === "bet-refunded"))
          this.groupResult = "bet-refunded";
        else if (results.every((r) => r === "resulted-draw"))
          this.groupResult = "resulted-draw";
        else if (results.every((r) => r === "bet-pending"))
          this.groupResult = "bet-pending";
        // mixed → giữ nguyên
      },
    },
  },
  methods: {
    ...mapActions({
      manualResultSgmBetslip: "trading/betslip/manualResultSgmBetslip",
    }),
    toResult(v) {
      if (!v) return null;
      if (v === "bet-pending") return "bet-pending";
      return RESULT_MAP[v] ?? v;
    },
    openModal(item) {
      this.sgmGroupId = item.groupBetId;
      this.groupOdds = item.odds;
      this.groupResult = this.toResult(item.result);
      this.legs = (item.selections || []).map((sel) => ({
        betslipEventId: sel.id,
        name: sel.name,
        marketName: sel.marketName,
        odds: sel.odds,
        result: this.toResult(sel.result),
      }));
      this.$refs.modal.show();
    },
    async handleSave() {
      if (this.legs.some((l) => !l.result)) {
        this.showToastError("Error", "Please set a result for every leg.");
        return;
      }
      if (!this.groupResult) {
        this.showToastError("Error", "Please set the group result.");
        return;
      }

      const payload = {
        betslipId: this.selectedBet.id,
        sgmGroups: [
          {
            sgmGroupId: this.sgmGroupId,
            result: this.groupResult,
            legs: this.legs.map((leg) => ({
              betslipEventId: leg.betslipEventId,
              result: leg.result,
            })),
          },
        ],
      };
      const ok = await this.manualResultSgmBetslip(payload);
      if (ok) {
        this.showToastSuccess("Success", "Update SGM result successfully!");
        this.$emit("success");
        this.$refs.modal.hide();
      }
    },
    hideModal() {
      this.sgmGroupId = null;
      this.groupOdds = null;
      this.groupResult = null;
      this.legs = [];
    },
  },
  setup() {
    const { showToastSuccess, showToastError } = useToastHandler();
    return { showToastSuccess, showToastError };
  },
};
</script>
